import { motion } from "framer-motion";
import { FiArrowRight, FiShield } from "react-icons/fi";
import { Link } from "react-router-dom";

const Section = ({ number, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-10"
  >
    <div className="flex items-start gap-4 mb-4">
      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 text-sm font-extrabold flex items-center justify-center">
        {number}
      </span>
      <h2 className="text-xl font-extrabold text-slate-900 pt-1">{title}</h2>
    </div>
    <div className="pl-12 text-[15px] text-slate-700 leading-relaxed space-y-3">
      {children}
    </div>
  </motion.div>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-3">
    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full mt-2 flex-shrink-0" />
    <span>{children}</span>
  </div>
);

const PrivacyPage = () => {
  const lastUpdated = "January 1, 2024";

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* ── Page Hero ── */}
      <div className="bg-white border-b-2 border-slate-100">
        <div className="container-main py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <FiShield size={20} className="text-indigo-600" />
              </div>
              <span className="text-xs font-extrabold uppercase tracking-widest text-indigo-600">
                Legal Document
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 font-display mb-3">
              Privacy Policy
            </h1>
            <p className="text-base text-slate-500 font-semibold mb-6">
              Last updated: {lastUpdated}
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
              At SiteVault, we take your privacy seriously. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your
              information when you use our website directory service. Please
              read this policy carefully.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Table of Contents + Content ── */}
      <div className="container-main py-12 lg:py-16">
        <div className="grid lg:grid-cols-4 gap-10 lg:gap-12 items-start">
          {/* Sticky Table of Contents */}
          <div className="lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl border-2 border-slate-100 p-5"
            >
              <h3 className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-4">
                Contents
              </h3>
              <nav className="space-y-1">
                {[
                  "Information We Collect",
                  "How We Use Your Information",
                  "Information Sharing",
                  "Cookies & Tracking",
                  "Data Security",
                  "Your Rights",
                  "Children's Privacy",
                  "Changes to This Policy",
                  "Contact Us",
                ].map((item, i) => (
                  <a
                    key={item}
                    href={`#section-${i + 1}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
                  >
                    <span className="text-[10px] font-extrabold text-slate-300 w-5">
                      {i + 1}
                    </span>
                    {item}
                  </a>
                ))}
              </nav>
            </motion.div>
          </div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-3"
          >
            <div id="section-1">
              <Section number="1" title="Information We Collect">
                <p>
                  We collect information you provide directly to us when you use
                  our service, create an account, submit a website, or contact
                  us for support.
                </p>
                <p className="font-semibold text-slate-800 mt-4 mb-2">
                  Account Information:
                </p>
                <div className="space-y-2">
                  {[
                    "Full name and email address",
                    "Password (stored in encrypted form)",
                    "Profile information you choose to add",
                    "Communication preferences",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="font-semibold text-slate-800 mt-4 mb-2">
                  Usage Information:
                </p>
                <div className="space-y-2">
                  {[
                    "Pages visited and features used",
                    "Search queries within our directory",
                    "Websites you bookmark or review",
                    "Device type, browser, and IP address",
                    "Referring URLs and exit pages",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="font-semibold text-slate-800 mt-4 mb-2">
                  User-Generated Content:
                </p>
                <div className="space-y-2">
                  {[
                    "Website submissions and descriptions",
                    "Reviews, ratings, and comments",
                    "Support messages and feedback",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-2">
              <Section number="2" title="How We Use Your Information">
                <p>
                  We use the information we collect for the following purposes:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Provide, operate, and improve our website directory service",
                    "Process and review website submissions",
                    "Display your reviews and ratings on listed websites",
                    "Send transactional emails such as account confirmations",
                    "Respond to your comments, questions, and support requests",
                    "Monitor and analyze usage patterns to enhance user experience",
                    "Detect and prevent fraudulent activity or abuse",
                    "Comply with applicable laws and legal obligations",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <div className="mt-4 p-4 bg-indigo-50 rounded-xl border-2 border-indigo-100">
                  <p className="text-sm font-bold text-indigo-800">
                    We do not use your information for advertising purposes or
                    sell it to third parties.
                  </p>
                </div>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-3">
              <Section number="3" title="Information Sharing">
                <p>
                  We do not sell, trade, or rent your personal information to
                  third parties. We may share your information only in the
                  following limited circumstances:
                </p>
                <div className="space-y-4 mt-4">
                  {[
                    {
                      title: "Service Providers",
                      desc: "We work with trusted third-party vendors who help us operate our service (e.g., hosting, email delivery). They are bound by confidentiality agreements.",
                    },
                    {
                      title: "Legal Requirements",
                      desc: "We may disclose information if required by law, court order, or government regulation, or to protect the rights and safety of SiteVault and its users.",
                    },
                    {
                      title: "Business Transfers",
                      desc: "In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction.",
                    },
                    {
                      title: "Your Consent",
                      desc: "We may share information with your explicit consent for any other purpose.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                    >
                      <p className="font-bold text-slate-900 mb-1">
                        {item.title}
                      </p>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-4">
              <Section number="4" title="Cookies & Tracking">
                <p>
                  We use cookies and similar tracking technologies to improve
                  your experience on SiteVault.
                </p>
                <div className="space-y-4 mt-4">
                  {[
                    {
                      type: "Essential Cookies",
                      desc: "Required for core functionality such as user authentication and session management. Cannot be disabled.",
                    },
                    {
                      type: "Analytics Cookies",
                      desc: "Help us understand how visitors interact with our site. We use this data to improve our service.",
                    },
                    {
                      type: "Preference Cookies",
                      desc: "Remember your settings and preferences for a better experience on return visits.",
                    },
                  ].map((c) => (
                    <div key={c.type} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-slate-800">{c.type}</p>
                        <p className="text-sm text-slate-600 mt-0.5">
                          {c.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4">
                  You can control cookies through your browser settings. Note
                  that disabling certain cookies may affect the functionality of
                  our service.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-5">
              <Section number="5" title="Data Security">
                <p>
                  We implement appropriate technical and organizational security
                  measures to protect your personal information, including:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Encryption of data in transit using HTTPS/TLS",
                    "Encrypted storage of passwords using bcrypt",
                    "Regular security audits and vulnerability testing",
                    "Access controls limiting employee data access",
                    "Automated monitoring for suspicious activity",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-4">
                  However, no method of transmission over the Internet or
                  electronic storage is 100% secure. While we strive to protect
                  your information, we cannot guarantee absolute security.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-6">
              <Section number="6" title="Your Rights">
                <p>
                  Depending on your location, you may have the following rights
                  regarding your personal information:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {[
                    {
                      right: "Access",
                      desc: "Request a copy of the data we hold about you",
                    },
                    {
                      right: "Correction",
                      desc: "Request correction of inaccurate or incomplete data",
                    },
                    {
                      right: "Deletion",
                      desc: "Request deletion of your personal data",
                    },
                    {
                      right: "Portability",
                      desc: "Receive your data in a structured, machine-readable format",
                    },
                    {
                      right: "Restriction",
                      desc: "Request restriction of processing in certain circumstances",
                    },
                    {
                      right: "Objection",
                      desc: "Object to processing of your data for certain purposes",
                    },
                  ].map((r) => (
                    <div
                      key={r.right}
                      className="p-4 bg-white rounded-xl border-2 border-slate-100"
                    >
                      <p className="font-extrabold text-slate-900 text-sm">
                        {r.right}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">{r.desc}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4">
                  To exercise any of these rights, contact us at{" "}
                  <a
                    href="mailto:privacy@sitevault.io"
                    className="text-indigo-600 font-bold hover:underline"
                  >
                    privacy@sitevault.io
                  </a>
                  . We will respond within 30 days.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-7">
              <Section number="7" title="Children's Privacy">
                <p>
                  SiteVault is not directed to children under the age of 13. We
                  do not knowingly collect personal information from children.
                  If you are a parent or guardian and believe your child has
                  provided us with personal information, please contact us
                  immediately at{" "}
                  <a
                    href="mailto:privacy@sitevault.io"
                    className="text-indigo-600 font-bold hover:underline"
                  >
                    privacy@sitevault.io
                  </a>{" "}
                  and we will delete such information.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-8">
              <Section number="8" title="Changes to This Policy">
                <p>
                  We may update this Privacy Policy from time to time. We will
                  notify you of any changes by:
                </p>
                <div className="space-y-2 mt-3">
                  {[
                    "Posting the new policy on this page",
                    'Updating the "Last updated" date at the top',
                    "Sending an email notification for significant changes",
                  ].map((i) => (
                    <Bullet key={i}>{i}</Bullet>
                  ))}
                </div>
                <p className="mt-3">
                  We encourage you to review this policy periodically. Continued
                  use of our service after changes constitutes acceptance of the
                  updated policy.
                </p>
              </Section>
            </div>

            <div className="border-t-2 border-slate-100 my-10" />

            <div id="section-9">
              <Section number="9" title="Contact Us">
                <p>
                  If you have any questions, concerns, or requests regarding
                  this Privacy Policy or your personal data, please contact us:
                </p>
                <div className="mt-4 p-6 bg-white rounded-2xl border-2 border-slate-100 space-y-3">
                  {[
                    {
                      label: "Email",
                      value: "privacy@sitevault.io",
                      href: "mailto:privacy@sitevault.io",
                    },
                    {
                      label: "General inquiries",
                      value: "hello@sitevault.io",
                      href: "mailto:hello@sitevault.io",
                    },
                  ].map((c) => (
                    <div
                      key={c.label}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-sm font-semibold text-slate-500">
                        {c.label}
                      </span>
                      <a
                        href={c.href}
                        className="text-sm font-bold text-indigo-600 hover:underline"
                      >
                        {c.value}
                      </a>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-primary">
                    Contact Us <FiArrowRight size={14} />
                  </Link>
                  <Link to="/terms" className="btn-secondary">
                    View Terms of Service
                  </Link>
                </div>
              </Section>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
